import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
    Network, Boxes, FileStack, FileSignature, Truck, ShieldAlert, BookOpen,
    ArrowUpRight, Layers, Sparkles,
} from "lucide-react";
import { api } from "@/lib/api";
import { EDI } from "@/constants/testIds";
import TwinObjectDrilldown from "@/components/edi/TwinObjectDrilldown";

const DOMAIN_ICON = {
    "Engineering":   Boxes,
    "Commercial":    FileSignature,
    "Contracts":     FileStack,
    "Procurement":   Truck,
    "Construction":  Layers,
    "Commissioning": Sparkles,
    "Quality":       ShieldAlert,
};

export default function DigitalEngineeringTwin() {
    const [projects, setProjects] = useState([]);
    const [selected, setSelected] = useState(null);
    const [twin, setTwin] = useState(null);
    const [busy, setBusy] = useState(false);
    const [drilldownTag, setDrilldownTag] = useState(null);

    useEffect(() => {
        api.get("/edi/twin/projects")
            .then((r) => {
                setProjects(r.data.projects || []);
                if (r.data.projects?.[0]) setSelected(r.data.projects[0].key);
            })
            .catch(() => toast.error("Failed to load twin projects"));
    }, []);

    useEffect(() => {
        if (!selected) return;
        setBusy(true);
        api.get(`/edi/twin/${selected}`)
            .then((r) => setTwin(r.data))
            .catch(() => toast.error("Failed to load twin"))
            .finally(() => setBusy(false));
    }, [selected]);

    return (
        <div className="space-y-6" data-testid={EDI.twinRoot}>
            <div className="glass rounded-xl p-6 relative overflow-hidden">
                <div
                    className="absolute inset-0 opacity-40 pointer-events-none"
                    style={{
                        background:
                            "radial-gradient(ellipse at 90% 10%, rgba(59,130,246,0.22), transparent 55%), radial-gradient(ellipse at 10% 90%, rgba(212,175,55,0.16), transparent 55%)",
                    }}
                />
                <div className="relative">
                    <div className="flex items-center gap-2 text-[var(--sgsb-gold)]">
                        <Network className="h-4 w-4" strokeWidth={1.5} />
                        <span className="kicker text-[10px]">Digital Engineering Twin™</span>
                    </div>
                    <h3 className="mt-1 font-display font-black text-white text-2xl tracking-tight">
                        Every engineering object — always linked, always current.
                    </h3>
                    <p className="text-slate-400 text-sm font-body mt-1 max-w-3xl">
                        A live enterprise model connecting engineering, commercial, contracts, procurement,
                        construction, commissioning and quality — with every drawing, contract, vendor,
                        lesson and risk attached to the object it belongs to.
                    </p>

                    <div className="mt-4 flex flex-wrap gap-2">
                        {projects.map((p) => (
                            <button
                                key={p.key}
                                data-testid={EDI.twinProjectSelect(p.key)}
                                onClick={() => setSelected(p.key)}
                                className={`inline-flex items-center gap-2 rounded-md px-3 py-1.5 text-xs font-display font-bold border transition-colors duration-200 ${
                                    selected === p.key
                                        ? "bg-[var(--sgsb-gold)] text-slate-950 border-[var(--sgsb-gold)]"
                                        : "border-slate-800 text-slate-300 hover:text-[var(--sgsb-gold)] hover:border-[color:var(--sgsb-gold)]/50"
                                }`}
                            >
                                {p.project}
                                <span className="text-[10px] opacity-70">{p.commodity}</span>
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {busy && !twin && <div className="kicker text-slate-500">Loading twin…</div>}

            {twin && (
                <>
                    {/* Twin headline */}
                    <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
                        {[
                            ["Project Progress", `${twin.progress_pct}%`, "Live"],
                            ["Reuse Rate",       `${twin.reuse_pct}%`,   "Baseline uplift"],
                            ["Documents Linked", twin.linked.documents.toLocaleString(), "Auto-classified"],
                            ["Drawings",         twin.linked.drawings.toLocaleString(),  "Cross-project"],
                            ["Vendors",          twin.linked.vendors,                    "Preferred"],
                            ["Risks Tracked",    twin.linked.risks,                      "Objects at risk"],
                        ].map(([k, v, s]) => (
                            <div key={k} className="glass hover-lift rounded-xl p-5 relative overflow-hidden">
                                <div className="absolute top-0 left-0 h-[2px] w-10 bg-[var(--sgsb-gold)]" />
                                <div className="kicker text-slate-400 text-[10px]">{k}</div>
                                <div className="mt-2 font-display font-black text-white text-2xl tracking-tighter">{v}</div>
                                <div className="mt-1 text-[11px] text-slate-500 font-body">{s}</div>
                            </div>
                        ))}
                    </div>

                    {/* Domain coverage */}
                    <div className="glass rounded-xl overflow-hidden">
                        <div className="p-4 border-b border-slate-800/60 flex items-center gap-2">
                            <Network className="h-4 w-4 text-[var(--sgsb-gold)]" strokeWidth={1.5} />
                            <h3 className="font-display font-black text-white tracking-tight">Domain Coverage — Twin Object Graph</h3>
                            <div className="flex-1" />
                            <span className="kicker text-[10px] text-slate-500">7 domains</span>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3 p-4">
                            {twin.domains.map((d) => {
                                const Icon = DOMAIN_ICON[d.domain] || Boxes;
                                return (
                                    <div key={d.domain} className="rounded-md border border-slate-800 bg-slate-950/40 p-4">
                                        <div className="flex items-center gap-3">
                                            <div className="h-9 w-9 rounded-md border border-[color:var(--sgsb-gold)]/40 bg-[color:var(--sgsb-gold)]/10 grid place-items-center">
                                                <Icon className="h-4 w-4 text-[var(--sgsb-gold)]" strokeWidth={1.5} />
                                            </div>
                                            <div>
                                                <div className="font-display font-black text-white text-sm tracking-tight">{d.domain}</div>
                                                <div className="text-[11px] text-slate-500 font-body">{d.objects.toLocaleString()} objects</div>
                                            </div>
                                            <div className="ml-auto text-[var(--sgsb-gold)] font-display font-bold text-sm">{d.coverage_pct}%</div>
                                        </div>
                                        <div className="mt-3 h-1.5 rounded-full bg-slate-800 overflow-hidden">
                                            <div className="h-full bg-[var(--sgsb-gold)]" style={{ width: `${d.coverage_pct}%` }} />
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* Top linked objects */}
                    <div className="glass rounded-xl overflow-hidden">
                        <div className="p-4 border-b border-slate-800/60 flex items-center gap-2">
                            <Boxes className="h-4 w-4 text-[var(--sgsb-gold)]" strokeWidth={1.5} />
                            <h3 className="font-display font-black text-white tracking-tight">Top Linked Objects</h3>
                            <div className="flex-1" />
                            <span className="kicker text-[10px] text-slate-500">Twin snapshot</span>
                        </div>
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm font-body">
                                <thead>
                                    <tr className="text-slate-500 kicker text-[10px]">
                                        <th className="text-left px-4 py-3">Object</th>
                                        <th className="text-left px-4 py-3">Tag</th>
                                        <th className="text-right px-4 py-3">Documents</th>
                                        <th className="text-right px-4 py-3">Vendors</th>
                                        <th className="text-right px-4 py-3">Lessons</th>
                                        <th className="text-right px-4 py-3">Risks</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {twin.top_assets.map((a) => (
                                        <tr
                                            key={a.tag}
                                            data-testid={EDI.twinAssetRow(a.tag)}
                                            onClick={() => setDrilldownTag(a.tag)}
                                            className="border-t border-slate-800/60 hover:bg-slate-900/40 transition-colors duration-150 cursor-pointer"
                                        >
                                            <td className="px-4 py-2 text-slate-100">
                                                <span className="inline-flex items-center gap-1.5">
                                                    <ArrowUpRight className="h-3 w-3 text-[var(--sgsb-gold)]" strokeWidth={2} />
                                                    {a.name}
                                                </span>
                                            </td>
                                            <td className="px-4 py-2 text-slate-400">{a.tag}</td>
                                            <td className="px-4 py-2 text-right text-slate-200">{a.docs}</td>
                                            <td className="px-4 py-2 text-right text-slate-400">{a.vendors}</td>
                                            <td className="px-4 py-2 text-right">
                                                <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/15 border border-emerald-500/40 px-2 py-0.5 text-[10px] text-emerald-400">
                                                    <BookOpen className="h-3 w-3" strokeWidth={2} />
                                                    {a.lessons}
                                                </span>
                                            </td>
                                            <td className="px-4 py-2 text-right">
                                                <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] border ${a.risks > 2 ? "border-red-500/40 text-red-400 bg-red-500/10" : a.risks > 0 ? "border-amber-500/40 text-amber-400 bg-amber-500/10" : "border-emerald-500/40 text-emerald-400 bg-emerald-500/10"}`}>
                                                    <ShieldAlert className="h-3 w-3" strokeWidth={2} />
                                                    {a.risks}
                                                </span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </>
            )}

            {drilldownTag && (
                <TwinObjectDrilldown tag={drilldownTag} onClose={() => setDrilldownTag(null)} />
            )}
        </div>
    );
}
